if (!customElements.get("m-icon-box")) {
  class MIconbox extends HTMLElement {
    constructor() {
      super();

      this.enableSlider = this.dataset.enableSlider === "true";
      this.items = this.dataset.items;
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.slideContainer = this.querySelector(".swiper-container");
    }

    connectedCallback() {
      this.lastActive = null;
      if (this.enableSlider) this.initSlider();
      if (this.closest("[data-mobile-loop]")) this.initMobileLoop();
    }

    // Rolagem horizontal sem fim: a lista vem repetida 3x do Liquid e, quando o
    // arrasto passa da última cópia (ou antes da primeira), devolvemos o scroll
    // uma cópia para trás/frente. Como o conteúdo é idêntico, não se vê o pulo.
    initMobileLoop() {
      const scroller = this.querySelector(".m-mixed-layout--mobile-scroll");
      const track = this.querySelector(".m-mixed-layout__inner");
      if (!scroller || !track) return;
      // em RTL o scrollLeft muda de sinal entre navegadores: não vale o risco
      if (document.documentElement.getAttribute("dir") === "rtl") return;

      const mql = window.matchMedia("(max-width: 767px)");
      let copyWidth = 0;
      let ticking = false;

      const measure = () => {
        const cols = track.children;
        const perCopy = porCopia();
        if (!perCopy || cols.length < perCopy * 2) return 0;
        return cols[perCopy].offsetLeft - cols[0].offsetLeft;
      };

      function porCopia() {
        return track.querySelectorAll(":scope > :not(.m-icon-box__loop-clone)").length;
      }

      /*
       * A rolagem fica entre 0 e uma copia (copyWidth). O autoplay desloca o
       * trilho em ate uma copia inteira (transform), entao o que aparece na tela
       * nunca passa da terceira copia, e o pulo de volta da animacao cai num
       * trecho identico: nao se ve emenda.
       */
      const onScroll = () => {
        if (ticking || !copyWidth) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const x = scroller.scrollLeft;
          if (x > copyWidth) scroller.scrollLeft = x - copyWidth;
          else if (x <= 0) scroller.scrollLeft = x + copyWidth;
        });
      };

      const enable = () => {
        copyWidth = measure();
        // se uma cópia inteira já cabe na tela não há o que rolar
        if (copyWidth <= 0 || copyWidth <= scroller.clientWidth) return disable();
        scroller.classList.add("is-looping");
        scroller.style.setProperty("--bs-loop-distance", copyWidth + "px");
        scroller.style.setProperty("--bs-loop-duration", (porCopia() * autoplayDelay) / 1000 + "s");
        scroller.scrollLeft = copyWidth;
        scroller.addEventListener("scroll", onScroll, { passive: true });
      };

      const disable = () => {
        copyWidth = 0;
        scroller.classList.remove("is-looping", "is-autoplay", "is-paused");
        scroller.removeEventListener("scroll", onScroll);
      };

      // ---------------------------------------------------------------- autoplay
      /*
       * Movimento continuo por animacao CSS no trilho (icon-box.css), que roda no
       * compositor. Antes era um requestAnimationFrame mexendo no scrollLeft a
       * cada quadro, desde o carregamento e mesmo fora da tela: quase 10 s de JS
       * no celular segundo o Lighthouse. Aqui o JS so liga, pausa e retoma.
       *
       * A velocidade sai do mesmo ajuste de segundos do editor: e o tempo que um
       * item leva para atravessar, entao a copia inteira leva itens x segundos.
       */
      const autoplayOn = this.dataset.enableAutoplay === "true";
      const autoplayDelay = (parseInt(this.dataset.autoplaySpeed) || 3) * 1000;
      const semMovimento = window.matchMedia("(prefers-reduced-motion: reduce)");
      let retomar = null;
      let visivel = !("IntersectionObserver" in window);
      let carregou = document.readyState === "complete";
      let segurando = false;

      const atualizar = () => {
        const pode = autoplayOn && !semMovimento.matches && carregou && scroller.classList.contains("is-looping");
        scroller.classList.toggle("is-autoplay", pode);
        scroller.classList.toggle("is-paused", !visivel || segurando || document.hidden);
      };

      // Enquanto a pessoa arrasta, o autoplay pausa e volta depois.
      const adiar = () => {
        segurando = true;
        atualizar();
        clearTimeout(retomar);
        retomar = setTimeout(() => {
          segurando = false;
          atualizar();
        }, autoplayDelay * 2);
      };

      ["pointerdown", "touchstart", "wheel"].forEach((ev) =>
        scroller.addEventListener(ev, adiar, { passive: true })
      );
      semMovimento.addEventListener("change", atualizar);
      document.addEventListener("visibilitychange", atualizar);

      const sync = () => {
        mql.matches ? enable() : disable();
        atualizar();
      };

      sync();
      mql.addEventListener("change", sync);
      // Girar o celular muda a largura sem cruzar o breakpoint: a faixa pode
      // passar a caber inteira (desliga o loop, senao sobra um vao no fim da
      // animacao) ou deixar de caber (liga). So troca de estado quando precisa,
      // porque enable() reposiciona a rolagem e o iOS dispara resize quando a
      // barra de endereco encolhe.
      window.addEventListener("resize", () => {
        if (!mql.matches) return;
        const largura = measure();
        const looping = scroller.classList.contains("is-looping");
        if (largura <= 0 || largura <= scroller.clientWidth) {
          if (looping) disable();
        } else if (!looping) {
          enable();
        } else {
          copyWidth = largura;
          scroller.style.setProperty("--bs-loop-distance", copyWidth + "px");
        }
        atualizar();
      });

      if (!carregou) {
        window.addEventListener("load", () => {
          carregou = true;
          atualizar();
        }, { once: true });
      }
      if ("IntersectionObserver" in window) {
        new IntersectionObserver((entries) => {
          visivel = entries[entries.length - 1].isIntersecting;
          atualizar();
        }).observe(scroller);
      }
    }

    initSlider() {
      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength = this.querySelector(".swiper-wrapper").childElementCount;
      let __this = this;

      // Mesmo contrato de brands-list.js: velocidade vem em segundos.
      // `disableOnInteraction: false` para a rotacao voltar depois que a pessoa
      // arrasta, em vez de parar de vez.
      const autoplay = this.dataset.enableAutoplay === "true";
      const autoplaySpeed = this.dataset.autoplaySpeed;

      this.slider = new MinimogLibs.Swiper(this.slideContainer, {
        slidesPerView: 1,
        navigation: this.showNavigation,
        loop: true,
        autoplay: autoplay
          ? {
              delay: parseInt(autoplaySpeed) * 1000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        pagination: this.showPagination
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          480: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: parseInt(this.items) - 1,
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        on: {
          init: function () {
            this.slideToLoop(this.lastActive);
            setTimeout(() => {
              // Calculate controls position
              const firstItem = __this.querySelector(".m-image") || __this.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton?.classList.remove("m:hidden");
                nextButton?.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => this.slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => this.slider.slideNext());
      }
      document.addEventListener("shopify:block:select", (e) => this.onBlockSelect(e));
    }

    onBlockSelect(e) {
      const block = e.target;
      const index = Number(block.dataset.slide);
      this.lastActive = index;
      if (this.slider) {
        this.slider.slideToLoop(index);
      }
    }
  }

  customElements.define("m-icon-box", MIconbox);
}
